/**
 * Run Stream Client — SSE client for draft workflow run monitoring.
 *
 * Connects to POST .../runDraftWithStream to receive real-time run events.
 * Uses the same fetch+getReader pattern as aguiStreamClient.ts.
 *
 * Key constraints:
 * - Never reconnect on error — POST creates a new run each time
 * - One-way fallback to polling on failure
 * - Only for draft workflows triggered by user clicking Run
 */

//import { isLocalMode } from './localRuntimeClient';
//import { buildStreamDraftRunUrl } from './monitoringApi';
import type { MonitoringFetchOptions } from './monitoringApi';
import { parseRunMonitoringEvent } from '@/types/streamingMonitoring';
import type { RunMonitoringEvent, RunStreamError } from '@/types/streamingMonitoring';
import { telemetryService } from './telemetryService';
import { extractErrorMessage } from './apiError';

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

export interface RunStreamCallbacks {
  /** Called for each successfully parsed monitoring event */
  onEvent: (event: RunMonitoringEvent) => void;
  /** Called when the stream ends normally (RunCompleted received or EOF) */
  onComplete: () => void;
  /** Called on any error — stream is dead after this, caller should fall back to polling */
  onError: (error: RunStreamError) => void;
  /**
   * Called immediately after the POST response is received (before SSE reading starts).
   * Provides the run ID from the `x-ms-workflow-run-id` response header if present.
   * This is the earliest point at which the run ID is available.
   */
  onPostSuccess?: (runId: string | undefined) => void;
}

export interface RunStreamOptions {
  /** Standard monitoring options (subscriptionId, hostName, workflowName, etc.) */
  fetchOptions: MonitoringFetchOptions;
  /** Name of the trigger to fire (typically "manual") */
  triggerName: string;
  /** URL to receive streaming events */
  streamCallbackUrl: string;
  /** Optional request body for the trigger (e.g., manual trigger inputs) */
  body?: unknown;
  /** AbortSignal to cancel the stream */
  signal?: AbortSignal;
  /** Event callbacks */
  callbacks: RunStreamCallbacks;
}

// ---------------------------------------------------------------------------
// Client
// ---------------------------------------------------------------------------

/**
 * Start a streaming draft run. Sends POST to runDraftWithStream and
 * reads the SSE response. Returns a promise that resolves when the
 * stream ends (either by RunCompleted, EOF, or error).
 *
 * The caller is responsible for:
 * - Providing an AbortController/signal for cancellation
 * - Falling back to polling on error
 * - NOT calling this again for the same run (POST creates a new run)
 */
export async function streamDraftRun(options: RunStreamOptions): Promise<void> {
  const { fetchOptions, streamCallbackUrl, body, signal, callbacks } = options;
  const { onEvent, onComplete, onError, onPostSuccess } = callbacks;

  // const url = buildStreamDraftRunUrl(fetchOptions, triggerName);

  // Build headers — same pattern as triggerDraftWorkflow
  const headers: Record<string, string> = {
    'Content-Type': 'application/json',
    'Accept': 'text/event-stream',
  };

  /*if (!isLocalMode()) {
    headers['Authorization'] = `Bearer ${fetchOptions.accessToken}`;
  }*/

  try {
    telemetryService.trackEvent('sse.runstream.connecting', {
      workflowName: fetchOptions.workflowName,
    });

    const response = await fetch(streamCallbackUrl, {
      method: 'POST',
      headers,
      ...(body !== undefined ? { body: JSON.stringify(body) } : {}),
      ...(signal ? { signal } : {}),
    });

    if (!response.ok) {
      const errorText = await response.text().catch(() => '');
      const detail = extractErrorMessage(errorText);
      const message = `HTTP ${response.status}: ${detail || errorText || 'Unknown error'}`;

      telemetryService.trackEvent('sse.runstream.http_error', {
        status: String(response.status),
        workflowName: fetchOptions.workflowName,
      });

      onError({
        type: response.status === 404 ? 'not-supported' : 'connection-failed',
        message,
        httpStatus: response.status,
      });
      return;
    }

    if (!response.body) {
      onError({
        type: 'connection-failed',
        message: 'No response body — SSE not available',
      });
      return;
    }

    telemetryService.trackEvent('sse.runstream.connected', {
      workflowName: fetchOptions.workflowName,
    });

    // Read the run ID from the response header immediately — this is the
    // earliest reliable source of the run ID, available before any SSE events.
    // Only notify callers when the backend actually provided a non-empty run ID;
    // older backends may omit this header, in which case callers should still be
    // able to fall back to discovering the run ID from the first SSE event.
    const responseRunId = response.headers.get('x-ms-workflow-run-id') || undefined;
    if (responseRunId) {
      onPostSuccess?.(responseRunId);
    }

    await readSseStream(response.body, signal, onEvent, onComplete, onError, fetchOptions.workflowName);
  } catch (err: unknown) {
    // User cancelled or stopStream called — the store already set 'aborted'
    // status; do NOT call onComplete (that would overwrite 'aborted' → 'completed').
    if (err instanceof Error && err.name === 'AbortError') {
      telemetryService.trackEvent('sse.runstream.aborted', {
        workflowName: fetchOptions.workflowName,
      });
      return;
    }

    const message = err instanceof Error ? err.message : String(err);
    telemetryService.trackEvent('sse.runstream.error', {
      workflowName: fetchOptions.workflowName,
    });
    onError({
      type: 'connection-failed',
      message,
    });
  }
}

// ---------------------------------------------------------------------------
// SSE Line Parser
// ---------------------------------------------------------------------------

/**
 * Read the SSE stream from a ReadableStream<Uint8Array>.
 *
 * SSE spec compliance: accumulates `data:` lines until a blank-line
 * delimiter before parsing. This handles multi-line JSON payloads
 * (e.g., when JToken error fields contain embedded newlines).
 *
 * We intentionally ignore `event:` and `id:` lines — kind is always
 * embedded in the JSON data field (Required.Always in C# model).
 * SSE reconnect (Last-Event-ID) is not supported — POST creates a new run.
 */
async function readSseStream(
  body: ReadableStream<Uint8Array>,
  _signal: AbortSignal | undefined,
  onEvent: (event: RunMonitoringEvent) => void,
  onComplete: () => void,
  onError: (error: RunStreamError) => void,
  workflowName: string,
): Promise<void> {
  const reader = body.getReader();
  const decoder = new TextDecoder();
  let buffer = '';
  let eventCount = 0;
  let parseErrorCount = 0;
  // Accumulate data: lines for the current SSE event (per SSE spec,
  // multiple data: lines are joined with \n before dispatch)
  let dataLines: string[] = [];

  try {
    while (true) {
      const { done, value } = await reader.read();

      if (done) {
        // Process any remaining buffer content (stream ended without trailing newline)
        if (buffer.trim()) {
          const trimmed = buffer.trim();
          if (trimmed.startsWith('data: ')) {
            dataLines.push(trimmed.slice(6));
          }
          buffer = '';
        }

        // Flush any accumulated data lines
        if (dataLines.length > 0) {
          const fullData = dataLines.join('\n');
          if (processDataPayload(fullData, onEvent, workflowName, () => { parseErrorCount++; })) {
            eventCount++;
          }
          dataLines = [];
        }

        telemetryService.trackEvent('sse.runstream.completed', {
          workflowName,
        }, {
          event_count: eventCount,
          parse_errors: parseErrorCount,
        });

        onComplete();
        return;
      }

      const chunk = decoder.decode(value, { stream: true });
      buffer += chunk;

      // Split on newlines, keep last (possibly incomplete) line in buffer
      const lines = buffer.split('\n');
      buffer = lines.pop() || '';

      for (const line of lines) {
        const trimmed = line.trim();

        if (!trimmed) {
          // Blank line = SSE event boundary. Parse accumulated data lines.
          if (dataLines.length > 0) {
            const fullData = dataLines.join('\n');
            if (processDataPayload(fullData, onEvent, workflowName, () => { parseErrorCount++; })) {
              eventCount++;
            }
            dataLines = [];
          }
          continue;
        }

        if (trimmed.startsWith(':')) {
          // SSE comment — skip
          continue;
        }

        if (trimmed.startsWith('data:')) {
          dataLines.push(trimmed.substring(5).trim());
        }
        // Ignore `event:`, `id:`, `retry:` lines
      }
    }
  } catch (err: unknown) {
    // Let AbortError propagate to the outer catch in streamDraftRun
    // for consolidated abort telemetry handling
    if (err instanceof Error && err.name === 'AbortError') {
      throw err;
    }

    const message = err instanceof Error ? err.message : String(err);
    onError({
      type: 'stream-error',
      message: `Stream read error: ${message}`,
    });
  } finally {
    reader.releaseLock();
  }
}

/**
 * Process an accumulated SSE data payload (may be multi-line if backend
 * split the JSON). Returns true if successfully parsed.
 */
function processDataPayload(
  data: string,
  onEvent: (event: RunMonitoringEvent) => void,
  workflowName: string,
  onParseError: () => void,
): boolean {
  const jsonStr = data.trim();
  if (!jsonStr) return false;

  const result = parseRunMonitoringEvent(jsonStr);
  if (result.success) {
    onEvent(result.event);
    return true;
  } else {
    telemetryService.trackEvent('sse.runstream.parse_error', {
      error: result.error,
      workflowName,
    });
    onParseError();
    return false;
  }
}
